import { BigNumber } from '@0x/utils'
import snapshotApi, { IDaoProposal } from 'app-lib/apis/snapshotApi'
import { Asset } from 'app-lib/assetUtils'
import { sleep } from 'app-lib/asyncUtils'
import errorUtils from 'app-lib/errorUtils'
import stakingUtils from 'app-lib/stakingUtils'
import { ZERO_ADDRESS } from 'bzx-common/src/config/appConfig'
import * as mobx from 'mobx'
import { IOnchainProposal } from '../../services/DaoService'
import RootStore from '../../stores/RootStore'

interface IDaoProps {
  autoSyncData: boolean
  delegateAddress: string
  getDelegateError: null | Error
  getDelegatePending: boolean
  globalStaked: { token: Asset; amount: BigNumber }[]
  proposalsError: null | Error
  proposalsPending: boolean
  rootStore: RootStore
  setDelegateError: null | Error
  setDelegatePending: boolean
  snapshotProposals: IDaoProposal[]
  votingPowerError: null | Error
  votingPowerPending: boolean
  votingPowerStaked: BigNumber
  votingPowerTotal: BigNumber
  onchainProposals: IOnchainProposal[]
  onchainProposalsPending: boolean
  onchainProposalsError: null | Error
}

export default class Dao implements IDaoProps {
  autoSyncData = false
  delegateAddress = ''
  getDelegateError: null | Error = null
  getDelegatePending = false
  globalStaked: { token: Asset; amount: BigNumber }[] = []
  proposalsError: null | Error = null
  proposalsPending = false
  rootStore: RootStore
  setDelegateError: null | Error = null
  setDelegatePending = false
  snapshotProposals: IDaoProposal[] = []
  votingPowerError = null
  votingPowerPending = false
  votingPowerStaked = new BigNumber(0)
  votingPowerTotal = new BigNumber(0)
  onchainProposals: IOnchainProposal[] = []
  onchainProposalsPending = false
  onchainProposalsError: null | Error = null

  get error() {
    return this.getDelegateError || this.setDelegateError || this.votingPowerError || null
  }

  get hasDelegate() {
    return this.delegateAddress && this.delegateAddress !== ZERO_ADDRESS
  }

  get votingPowerFromDelegators() {
    if (this.votingPowerStaked.eq(this.votingPowerTotal)) {
      return new BigNumber(0)
    }
    return this.votingPowerTotal.minus(this.votingPowerStaked)
  }

  get onchainProposalOngoing() {
    return this.onchainProposals.some((proposal) => !proposal.canceled && !proposal.executed)
  }

  /**
   * Flag to tell user that he needs to vote before staking or claiming rewards.
   * This is due to a bug related to staking contract that causes people to lose their voting power.
   */
  get userShouldVote() {
    return this.votingPowerTotal.gt(100000) && this.onchainProposalOngoing
  }

  set(prop: keyof IDaoProps, value: any) {
    ;(this[prop] as any) = value
  }

  assign(props: Partial<IDaoProps>) {
    Object.assign(this, props)
  }

  async getVotingPower() {
    try {
      const { fulcrumProvider: fp } = this.rootStore
      this.assign({ votingPowerPending: true, votingPowerError: null })
      const votingPower = await stakingUtils.getVotingPower(fp)
      this.assign(votingPower)
    } catch (err) {
      this.set(
        'votingPowerError',
        errorUtils.decorateError(err, { title: 'Could not get your voting power' })
      )
      console.error(err)
    } finally {
      this.assign({ votingPowerPending: false })
    }
  }

  async getDelegate() {
    try {
      const { fulcrumProvider: fp } = this.rootStore
      this.assign({ getDelegatePending: true, getDelegateError: null })
      const delegateAddress = await stakingUtils.getDelegate(fp)
      this.assign({ delegateAddress })
    } catch (err) {
      this.set(
        'getDelegateError',
        errorUtils.decorateError(err, { title: 'Could not get delegate info' })
      )
      console.error(err)
    } finally {
      this.assign({ getDelegatePending: false })
    }
  }

  async setDelegate(delegateAddress: string) {
    try {
      const { transactionStore, fulcrumProvider } = this.rootStore
      this.assign({ setDelegatePending: true, setDelegateError: null })
      const txSteps = stakingUtils.delegate(fulcrumProvider, delegateAddress)
      const tx = await transactionStore.run('delegate', txSteps)
      if (tx.status === 'success') {
        this.assign({ delegateAddress })
      }
      return tx
    } catch (err) {
      this.assign({
        setDelegateError: errorUtils.decorateError(err, { title: 'Could not delegate' }),
      })
    } finally {
      this.assign({ setDelegatePending: false })
    }
  }

  async getSnapshotProposals() {
    try {
      this.assign({ proposalsError: null, proposalsPending: true })
      const snapshotProposals = await snapshotApi.getProposals()
      snapshotProposals.forEach((prop) => {
        // Some proposals start with "Ooki Snapshot Vote for"; tries to simplify titles
        prop.title = prop.title.replace(/^Ooki Snapshot|Vote for/gi, '').trim()
        prop.start = prop.start * 1000 // dates from api are in ms
        prop.end = prop.end * 1000
      })
      this.assign({ snapshotProposals })
    } catch (proposalsError) {
      this.assign({ proposalsError })
    } finally {
      this.assign({ proposalsPending: false })
    }
  }

  async getGlobalStake() {
    try {
      const { fulcrumProvider: fp } = this.rootStore
      const globalStaked = await stakingUtils.getGlobalStaked(fp)
      this.assign({ globalStaked })
    } catch (err) {
      // Fail silently; global stats aren't that important
      console.error(err)
    }
  }

  async getOnchainProposals() {
    try {
      this.assign({ onchainProposalsPending: true, onchainProposalsError: null })
      const { daoService } = this.rootStore.services
      const onchainProposals = await daoService.getOnchainProposals()
      this.assign({ onchainProposals })
    } catch (err) {
      const onchainProposalsError = errorUtils.decorateError(err, {
        title: 'Could not fetch onchain proposals',
      })
      this.assign({ onchainProposalsError })
    } finally {
      this.assign({ onchainProposalsPending: false })
    }
  }

  stopDelegating() {
    return this.setDelegate(ZERO_ADDRESS)
  }

  clearError() {
    this.setDelegateError = null
    this.getDelegateError = null
    this.votingPowerError = null
  }

  clearData() {
    this.delegateAddress = ''
    this.votingPowerStaked = new BigNumber(0)
    this.votingPowerTotal = new BigNumber(0)
    this.clearError()
  }

  async syncData(walletAddress: string) {
    if (walletAddress) {
      await this.getVotingPower()
      await sleep(250)
      await this.getDelegate()
      await sleep(250)
    } else {
      this.clearData()
    }
    await this.getGlobalStake()
  }

  init() {
    mobx.autorun(() => {
      const { chainId, walletAddress } = this.rootStore.web3Connection
      if (this.autoSyncData) {
        if (chainId === 1) {
          this.syncData(walletAddress)
        } else {
          this.clearData()
        }
      }
    })
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}
